import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export type BrandId = 'sushiki' | 'donvito';

export interface BrandConfig {
  id: BrandId;
  name: string;
  shortName: string;
  domain: string;
  otherBrand: BrandId;
  // Palette
  primary: string;
  primaryDark: string;
  accent: string;
  bg: string;
  bgElev: string;
  text: string;
  textMuted: string;
  // Typography
  fontDisplay: string;
  fontBody: string;
  // Assets
  logo: string;
  logoDark?: string;
  favicon: string;
  // Copy
  tagline: { de: string; en: string };
  subTagline: { de: string[]; en: string[] };
  heroKicker: { de: string; en: string };
  storyTitle: { de: string; en: string };
  storyBody: { de: string; en: string };
  storyQuote: { de: string; en: string };
  crossPromoTitle: { de: string; en: string };
  crossPromoText: { de: string; en: string };
  crossPromoCta: { de: string; en: string };
  // Meta
  metaTitle: { de: string; en: string };
  metaDescription: { de: string; en: string };
}

const SUSHIKI: BrandConfig = {
  id: 'sushiki',
  name: 'SushiKi',
  shortName: 'SushiKi',
  domain: 'sushiki-huerth.de',
  otherBrand: 'donvito',
  primary: '#94e815',
  primaryDark: '#6fb310',
  accent: '#f4f4f0',
  bg: '#0a0a0a',
  bgElev: '#141414',
  text: '#f4f4f0',
  textMuted: '#a1a1a1',
  fontDisplay: '"Inter Tight", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  fontBody: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  logo: '/sushiki_emblem_white.svg',
  logoDark: '/sushiki_emblem_black.svg',
  favicon: '/sushiki_favicon.ico',
  tagline: {
    de: 'Fresh Premium Sushi',
    en: 'Fresh Premium Sushi',
  },
  subTagline: {
    de: [
      'Handgerollt in Hürth.',
      'Jeden Morgen frisch zubereitet.',
      'Premium Qualität, täglich.',
    ],
    en: [
      'Hand-rolled in Hürth.',
      'Prepared fresh every morning.',
      'Premium quality, every day.',
    ],
  },
  heroKicker: {
    de: 'Japanische Küche · Hürth',
    en: 'Japanese Kitchen · Hürth',
  },
  storyTitle: {
    de: 'Unsere Geschichte',
    en: 'Our Story',
  },
  storyBody: {
    de: 'Bei SushiKi verbinden wir traditionelles japanisches Handwerk mit modernem Geschmack. Jede Rolle, jedes Nigiri wird von unseren erfahrenen Köchen zubereitet — mit Zutaten, die wir täglich frisch auswählen. Keine Kompromisse. Kein Standard. Nur echte Qualität.',
    en: 'At SushiKi we combine traditional Japanese craft with modern taste. Every roll, every nigiri is prepared by our experienced chefs — with ingredients we hand-pick fresh each morning. No compromises. No shortcuts. Just real quality.',
  },
  storyQuote: {
    de: '„Der Fisch muss so frisch sein, als käme er gerade aus dem Ozean." — Chef Taka',
    en: '"The fish must be as fresh as if it just came from the ocean." — Chef Taka',
  },
  crossPromoTitle: {
    de: 'Lust auf Pizza heute Abend?',
    en: 'Craving pizza tonight?',
  },
  crossPromoText: {
    de: 'Unsere italienische Schwestermarke Don Vito liefert authentische Steinofen-Pizza direkt an deine Tür. Bestelle beides in einer Lieferung.',
    en: 'Our Italian sister brand Don Vito delivers authentic stone-oven pizza right to your door. Order both in one delivery.',
  },
  crossPromoCta: {
    de: 'Pizza entdecken',
    en: 'Discover Pizza',
  },
  metaTitle: {
    de: 'SushiKi Hürth — Premium Sushi-Lieferung',
    en: 'SushiKi Hürth — Premium Sushi Delivery',
  },
  metaDescription: {
    de: 'Frisches, handgerolltes Sushi direkt aus Hürth. Online bestellen — Lieferung oder Abholung.',
    en: 'Fresh, hand-rolled sushi from Hürth. Order online — delivery or pickup.',
  },
};

const DONVITO: BrandConfig = {
  id: 'donvito',
  name: 'Pizza Don Vito',
  shortName: 'Don Vito',
  domain: 'donvito-huerth.de',
  otherBrand: 'sushiki',
  // Monochrome-editorial palette inspired by the black/white Pizza Don Vito
  // wordmark. Cream = primary brand color (like the paper in the logo),
  // amber = Italian warmth accent, near-black bg keeps it cinematic.
  primary: '#f5ead1',
  primaryDark: '#e0d3ad',
  accent: '#e8a94b',
  bg: '#0a0807',
  bgElev: '#1a1510',
  text: '#f5ead1',
  textMuted: '#8a7d65',
  fontDisplay: '"Archivo Black", "Inter Tight", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  fontBody: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  logo: '/donvito_logo.png',
  favicon: '/donvito_favicon.ico',
  tagline: {
    de: 'Autentico. Artigianale.',
    en: 'Autentico. Artisanal.',
  },
  subTagline: {
    de: [
      'Aus dem Steinofen.',
      'Mit Liebe gemacht.',
      'Wie in Napoli.',
    ],
    en: [
      'From the stone oven.',
      'Made with love.',
      'Just like in Napoli.',
    ],
  },
  heroKicker: {
    de: 'Italienische Küche · Hürth',
    en: 'Italian Kitchen · Hürth',
  },
  storyTitle: {
    de: 'La Nostra Storia',
    en: 'Our Story',
  },
  storyBody: {
    de: 'Pizza Don Vito ist ein Stück Italien in Hürth. Unser Teig ruht 48 Stunden, wird von Hand ausgezogen und im Steinofen bei 400°C in 90 Sekunden gebacken. San-Marzano-Tomaten, Fior di Latte, Basilikum aus Campania — original italienische Zutaten, ohne Kompromisse.',
    en: 'Pizza Don Vito is a piece of Italy in Hürth. Our dough rests 48 hours, is stretched by hand, and baked at 400°C for 90 seconds in a stone oven. San Marzano tomatoes, fior di latte, basil from Campania — original Italian ingredients, no compromises.',
  },
  storyQuote: {
    de: '„Una pizza buona si sente dal primo morso." — Don Vito',
    en: '"A good pizza can be tasted in the first bite." — Don Vito',
  },
  crossPromoTitle: {
    de: 'Auch Lust auf Sushi?',
    en: 'Also craving sushi?',
  },
  crossPromoText: {
    de: 'Unsere Schwestermarke SushiKi bietet frisches, handgerolltes Sushi — liefern wir in derselben Bestellung. Kombiniere, was du magst.',
    en: 'Our sister brand SushiKi offers fresh, hand-rolled sushi — delivered in the same order. Mix and match what you love.',
  },
  crossPromoCta: {
    de: 'Sushi entdecken',
    en: 'Discover Sushi',
  },
  metaTitle: {
    de: 'Pizza Don Vito Hürth — Authentische italienische Pizza',
    en: 'Pizza Don Vito Hürth — Authentic Italian Pizza',
  },
  metaDescription: {
    de: 'Steinofen-Pizza aus Hürth, nach Originalrezept aus Napoli. Online bestellen — Lieferung oder Abholung.',
    en: 'Stone-oven pizza from Hürth, original recipe from Napoli. Order online — delivery or pickup.',
  },
};

function detectBrandFromPath(pathname: string): BrandId | null {
  const p = (pathname || '').toLowerCase();
  // Matches /menu/donvito, /menu/pizza-don-vito, /menu/sushiki
  if (/\/menu\/(donvito|pizza[-_]?don[-_]?vito)/.test(p)) return 'donvito';
  if (/\/menu\/sushiki/.test(p)) return 'sushiki';
  return null;
}

const BRAND_STORAGE_KEY = 'sushikiug:brand';

function detectBrand(pathname?: string): BrandId {
  if (typeof window === 'undefined') return 'sushiki';
  // 1. URL path param wins (e.g. /table/1/menu/donvito)
  const fromPath = detectBrandFromPath(pathname || window.location.pathname);
  if (fromPath) return fromPath;
  // 2. Query string override
  const params = new URLSearchParams(window.location.search);
  const override = params.get('brand');
  if (override === 'donvito' || override === 'sushiki') return override;
  // 3. Hostname (per-domain deploys)
  const host = window.location.hostname.toLowerCase();
  if (host.includes('donvito')) return 'donvito';
  if (host.includes('sushiki-huerth')) return 'sushiki';
  // 4. Last-seen brand in this session (so refreshing on /cart keeps you in
  //    the brand you entered via /menu/donvito)
  try {
    const stored = sessionStorage.getItem(BRAND_STORAGE_KEY);
    if (stored === 'donvito' || stored === 'sushiki') return stored;
  } catch { /* storage blocked */ }
  return 'sushiki';
}

interface BrandContextValue {
  brand: BrandConfig;
  other: BrandConfig;
  switchTo: (id: BrandId) => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [brandId, setBrandId] = useState<BrandId>(() => detectBrand());

  // Re-detect brand on every path change — but ONLY flip brands when the new
  // path/query carries an explicit brand hint. Inner pages like /cart or
  // /checkout don't carry a hint, so we must keep the previous brand instead
  // of falling back to the hostname default (which was flipping Don Vito
  // guests back to SushiKi the moment they opened Cart).
  useEffect(() => {
    const fromPath = detectBrandFromPath(location.pathname);
    if (fromPath) {
      setBrandId(fromPath);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const override = params.get('brand');
    if (override === 'donvito' || override === 'sushiki') {
      setBrandId(override);
    }
    // else: keep the brand that's already in state (sticky)
  }, [location.pathname]);

  // Persist the current brand to sessionStorage so refreshing on an inner
  // page (e.g. /cart) can recover it in detectBrand() above.
  useEffect(() => {
    try { sessionStorage.setItem(BRAND_STORAGE_KEY, brandId); } catch { /* ignore */ }
  }, [brandId]);

  const value = useMemo(() => {
    const brand = brandId === 'donvito' ? DONVITO : SUSHIKI;
    const other = brandId === 'donvito' ? SUSHIKI : DONVITO;
    return {
      brand,
      other,
      switchTo: (target: BrandId) => {
        const targetDomain = target === 'donvito' ? DONVITO.domain : SUSHIKI.domain;
        if (window.location.hostname.includes('web.app') || window.location.hostname === 'localhost') {
          const url = new URL(window.location.href);
          url.searchParams.set('brand', target);
          window.location.href = url.toString();
        } else {
          window.location.href = `https://${targetDomain}`;
        }
      },
    };
  }, [brandId]);

  // Inject brand CSS vars + body class. Every rule in index.css that reads
  // --brand-primary / --brand-bg / --brand-font-display will re-theme on load.
  useEffect(() => {
    const r = document.documentElement;
    r.style.setProperty('--brand-primary', value.brand.primary);
    r.style.setProperty('--brand-primary-dark', value.brand.primaryDark);
    r.style.setProperty('--brand-accent', value.brand.accent);
    r.style.setProperty('--brand-bg', value.brand.bg);
    r.style.setProperty('--brand-bg-elev', value.brand.bgElev);
    r.style.setProperty('--brand-text', value.brand.text);
    r.style.setProperty('--brand-text-muted', value.brand.textMuted);
    r.style.setProperty('--brand-font-display', value.brand.fontDisplay);
    r.style.setProperty('--brand-font-body', value.brand.fontBody);

    // Body class so CSS can do brand-specific overrides.
    document.body.classList.remove('brand-sushiki', 'brand-donvito');
    document.body.classList.add(`brand-${value.brand.id}`);

    // Document title + favicon for the right brand.
    const lang = (navigator.language || 'de').startsWith('de') ? 'de' : 'en';
    document.title = value.brand.metaTitle[lang as 'de' | 'en'] || document.title;
    const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (link && value.brand.favicon) link.href = value.brand.favicon;
  }, [value]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

export function useBrand(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
}

export { SUSHIKI, DONVITO };
